// Admin v2 — Fizetések (Barion): minden online fizetés és visszatérítés élőben, a payment-service adataiból.
// Forrás: payments, payments/{id}/events (auditnapló), refunds — írni csak a szerver tud, az admin olvas.
import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, getDocs, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { fmtInt, toDate } from '../data';
import { Badge, Button, Card, Empty, Input, Modal, PageHeader, Select, Spinner, StatCard, Tabs } from '../ui';
import type { Tone } from '../ui';

type PStatus = 'started' | 'succeeded' | 'failed';
interface Pay {
  id: string;
  kind: 'match' | 'board';
  uid: string;
  clubId: string;
  matchId: string | null;
  bookingId: string | null;
  amount: number;
  split: { club: number; dartiqon: number; xp: number; xpPlayer: number; xpCharity: number };
  status: PStatus;
  captured: boolean;
  keptAmount: number | null;
  refundedAmount: number;
  orphan: boolean;
  mode: 'sandbox' | 'live';
  barionPaymentId: string | null;
  barionStatus: string;
  failReason: string;
  createdAt: Date | null;
  startAt: Date | null;
  itemName: string;
  playerName: string;
}
interface Refund {
  id: string;
  paymentId: string;
  uid: string;
  clubId: string;
  amount: number;
  keep: number;
  reason: string;
  method: string;
  status: 'started' | 'succeeded' | 'failed';
  attempts: number;
  lastError: string;
  createdAt: Date | null;
  deadlineAt: Date | null;
}
const PAYMENTS_URL = (process.env.REACT_APP_PAYMENTS_URL || 'https://dartiqon-payments-yj5mgny34a-ew.a.run.app').replace(/\/$/, '');

/** Csak sandbox: letétben lévő fizetés azonnali lezárása a fizetési szerveren (a klub alfizetésének kipróbálásához). */
async function settleNow(paymentId: string): Promise<string> {
  const u = auth.currentUser;
  if (!u) throw new Error('Nem vagy bejelentkezve.');
  const res = await fetch(`${PAYMENTS_URL}/api/admin/settle-now`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await u.getIdToken()}` },
    body: JSON.stringify({ paymentId }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(String(data.error || `A lezárás nem sikerült (${res.status}).`));
  return String(data.status || '');
}

interface Ev { id: string; type: string; at: Date | null; data: Record<string, unknown> }

const num = (v: unknown) => (typeof v === 'number' ? v : Number(v ?? 0) || 0);
const ft = (n: number) => `${fmtInt(n)} Ft`;
const dt = (d: Date | null) => (d ? d.toLocaleString('hu-HU', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');

const STATUS: Record<PStatus, { label: string; tone: Tone }> = {
  started: { label: 'Fizetés indítva', tone: 'gold' },
  succeeded: { label: 'Fizetés sikeres', tone: 'green' },
  failed: { label: 'Fizetés sikertelen', tone: 'red' },
};
/** Teljes összegű lezárásnál (0 Ft vissza) nem visszatérítés történt, hanem lezárás. */
const rLabel = (r: Refund) => (r.amount === 0 && r.method === 'finish'
  ? { started: 'Lezárás indítva', succeeded: 'Lezárás sikeres', failed: 'Lezárás sikertelen' }[r.status]
  : R_STATUS[r.status].label);
const R_STATUS: Record<Refund['status'], { label: string; tone: Tone }> = {
  started: { label: 'Visszatérítés indítva', tone: 'gold' },
  succeeded: { label: 'Visszatérítés sikeres', tone: 'green' },
  failed: { label: 'Visszatérítés sikertelen', tone: 'red' },
};
const REASON: Record<string, string> = {
  player_cancelled: 'Játékos lemondta',
  club_cancelled: 'Klub lemondta',
  match_cancelled: 'Meccs törölve',
  fill_deadline: 'Nem telt be a határidőig',
  board_taken: 'A tábla foglalt lett',
  board_unavailable: 'Nincs szabad tábla',
  played: 'Lezárás a meccs után',
  orphan: 'Betelt / megszűnt hely',
  unavailable: 'Betelt / megszűnt hely',
  duplicate: 'Dupla fizetés',
};
const EV_LABEL: Record<string, string> = {
  created: 'Fizetés létrehozva',
  barion_start_request: 'Barion: fizetés indítása',
  barion_started: 'Barion: fizetési oldal kész',
  barion_start_failed: 'Barion: indítás sikertelen',
  callback: 'Barion visszajelzés',
  paid_applied: 'Sikeres fizetés könyvelve',
  failed: 'Fizetés sikertelen',
  refund_succeeded: 'Visszatérítés / lezárás sikeres',
  refund_failed: 'Visszatérítés sikertelen',
  admin_settle_now: 'Admin: azonnali lezárás (teszt)',
  finish_request: 'Barion: lezárás kérése',
};

// a payment-service rules.split_amount tükre: 5% Dartiqon, 1% XP (fele játékos, fele Charityqon), a többi a klubé
const rnd = (x: number) => Math.floor(x + 0.5);
function splitOf(amount: number) {
  const a = Math.max(0, rnd(amount));
  const dartiqon = rnd(a * 0.05);
  const xp = rnd(a * 0.01);
  const xpPlayer = Math.floor(xp / 2);
  return { amount: a, club: a - dartiqon - xp, dartiqon, xp, xpPlayer, xpCharity: xp - xpPlayer };
}
/** Az elszámolás alapja: lezárva a megtartott összeg, letétben a teljes befizetés (várható). */
const baseOf = (p: Pay) => (p.captured ? (p.keptAmount ?? p.amount) : p.amount - p.refundedAmount);

function payFrom(id: string, v: Record<string, unknown>): Pay {
  const split = (v.split as Record<string, unknown>) ?? {};
  const b = (v.barion as Record<string, unknown>) ?? {};
  return {
    id,
    kind: v.kind === 'board' ? 'board' : 'match',
    uid: String(v.uid ?? ''),
    clubId: String(v.clubId ?? ''),
    matchId: (v.matchId as string) ?? null,
    bookingId: (v.bookingId as string) ?? null,
    amount: num(v.amount),
    split: { club: num(split.club), dartiqon: num(split.dartiqon), xp: num(split.xp), xpPlayer: num(split.xpPlayer), xpCharity: num(split.xpCharity) },
    status: (['started', 'succeeded', 'failed'].includes(String(v.status)) ? v.status : 'started') as PStatus,
    captured: Boolean(v.captured),
    keptAmount: v.keptAmount == null ? null : num(v.keptAmount),
    refundedAmount: num(v.refundedAmount),
    orphan: Boolean(v.orphan),
    mode: v.barionMode === 'live' ? 'live' : 'sandbox',
    barionPaymentId: (v.barionPaymentId as string) ?? null,
    barionStatus: String(b.status ?? ''),
    failReason: String(v.failReason ?? ''),
    createdAt: toDate(v.createdAt),
    startAt: toDate(v.startAt),
    itemName: String(v.itemName ?? ''),
    playerName: String(v.playerName ?? ''),
  };
}
function refundFrom(id: string, v: Record<string, unknown>): Refund {
  return {
    id,
    paymentId: String(v.paymentId ?? ''),
    uid: String(v.uid ?? ''),
    clubId: String(v.clubId ?? ''),
    amount: num(v.amount),
    keep: num(v.keep),
    reason: String(v.reason ?? ''),
    method: String(v.method ?? ''),
    status: (['started', 'succeeded', 'failed'].includes(String(v.status)) ? v.status : 'started') as Refund['status'],
    attempts: num(v.attempts),
    lastError: String(v.lastError ?? ''),
    createdAt: toDate(v.createdAt),
    deadlineAt: toDate(v.deadlineAt),
  };
}

type Tab = 'payments' | 'refunds';
type Period = '7' | '30' | '90' | 'all';

export default function PaymentsPage() {
  const [tab, setTab] = useState<Tab>('payments');
  const [pays, setPays] = useState<Pay[] | null>(null);
  const [refunds, setRefunds] = useState<Refund[]>([]);
  const [err, setErr] = useState('');
  const [names, setNames] = useState<Record<string, string>>({});
  const [clubs, setClubs] = useState<Record<string, string>>({});
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<'all' | PStatus>('all');
  const [mode, setMode] = useState<'all' | 'sandbox' | 'live'>('all');
  const [period, setPeriod] = useState<Period>('30');
  const [open, setOpen] = useState<Pay | null>(null);

  // élő adatok — a legutóbbi 500 fizetés és visszatérítés
  useEffect(() => onSnapshot(query(collection(db, 'payments'), orderBy('createdAt', 'desc'), limit(500)),
    (s) => setPays(s.docs.map((d) => payFrom(d.id, d.data()))),
    (e) => { setErr(e.message); setPays([]); }), []);
  useEffect(() => onSnapshot(query(collection(db, 'refunds'), orderBy('createdAt', 'desc'), limit(500)),
    (s) => setRefunds(s.docs.map((d) => refundFrom(d.id, d.data()))), () => setRefunds([])), []);
  useEffect(() => {
    getDocs(collection(db, 'clubs')).then((s) => setClubs(Object.fromEntries(s.docs.map((d) => [d.id, String(d.data().name ?? 'Klub')])))).catch(() => undefined);
  }, []);
  // a fizetők neve (users/{uid}.username), csak a hiányzókat kérjük le
  useEffect(() => {
    const need = Array.from(new Set([...(pays ?? []).map((p) => p.uid), ...refunds.map((r) => r.uid)])).filter((u) => u && !(u in names));
    if (!need.length) return;
    Promise.all(need.slice(0, 60).map(async (u) => {
      const s = await getDoc(doc(db, 'users', u)).catch(() => null);
      return [u, String(s?.data()?.username ?? s?.data()?.displayName ?? u.slice(0, 8))] as const;
    })).then((rows) => setNames((n) => ({ ...n, ...Object.fromEntries(rows) })));
  }, [pays, refunds, names]);

  const since = period === 'all' ? 0 : Date.now() - Number(period) * 864e5;
  const inPeriod = (d: Date | null) => !d || d.getTime() >= since;
  const needle = q.trim().toLowerCase();
  const who = (p: { uid: string }) => names[p.uid] ?? '';
  const clubName = (id: string) => clubs[id] ?? '—';

  const shown = useMemo(() => (pays ?? []).filter((p) =>
    inPeriod(p.createdAt)
    && (status === 'all' || p.status === status)
    && (mode === 'all' || p.mode === mode)
    && (!needle || `${who(p)} ${p.playerName} ${clubName(p.clubId)} ${p.id} ${p.barionPaymentId ?? ''}`.toLowerCase().includes(needle)),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [pays, status, mode, needle, since, names, clubs]);

  const shownRefunds = useMemo(() => refunds.filter((r) =>
    inPeriod(r.createdAt) && r.reason !== 'played'
    && (!needle || `${who(r)} ${clubName(r.clubId)} ${r.paymentId}`.toLowerCase().includes(needle)),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [refunds, needle, since, names, clubs]);

  const stats = useMemo(() => {
    const ok = shown.filter((p) => p.status === 'succeeded' && !p.orphan);
    // a szerver lezáráskor a megtartott összegre számolja újra a megosztást (split)
    const gross = ok.reduce((s, p) => s + p.amount, 0);
    const parts = ok.map((p) => splitOf(baseOf(p)));
    const dq = parts.reduce((s, x) => s + x.dartiqon, 0);
    const xp = parts.reduce((s, x) => s + x.xp, 0);
    const club = parts.reduce((s, x) => s + x.club, 0);
    return {
      gross, dq, xp, club,
      okN: ok.length,
      failedN: shown.filter((p) => p.status === 'failed').length,
      refunded: shown.reduce((s, p) => s + p.refundedAmount, 0),
      pendingRefunds: refunds.filter((r) => r.status !== 'succeeded').length,
    };
  }, [shown, refunds]);

  return (
    <div>
      <PageHeader
        title="Fizetések"
        sub="Barion online fizetések, lezárások és visszatérítések — élőben, a fizetési szolgáltatás adataiból."
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <StatCard icon="Coins" label="Befizetett (bruttó)" value={ft(stats.gross)} />
        <StatCard icon="Buildings" tone="purple" label="Klubokhoz került" value={ft(stats.club)} />
        <StatCard icon="ChartLineUp" tone="green" label="Dartiqon-rész (5%) + XP-keret (1%)" value={`${ft(stats.dq)} + ${ft(stats.xp)}`} />
        <StatCard icon="ArrowCounterClockwise" tone="gold" label="Visszatérítve" value={ft(stats.refunded)} />
      </div>

      <Card className="mb-5">
        <div className="flex flex-wrap items-center gap-3">
          <Tabs value={tab} onChange={setTab} options={[
            { value: 'payments', label: `Fizetések (${shown.length})` },
            { value: 'refunds', label: `Visszatérítések (${shownRefunds.length})` },
          ]} />
          <div className="flex-1 min-w-[220px]">
            <Input icon="MagnifyingGlass" placeholder="Keresés: játékos, klub, azonosító…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <Select value={period} onChange={setPeriod} options={[
            { value: '7', label: 'Utolsó 7 nap' }, { value: '30', label: 'Utolsó 30 nap' },
            { value: '90', label: 'Utolsó 90 nap' }, { value: 'all', label: 'Teljes időszak' },
          ]} />
          {tab === 'payments' && (
            <Select value={status} onChange={setStatus} options={[
              { value: 'all', label: 'Minden állapot' }, { value: 'succeeded', label: 'Sikeres' },
              { value: 'started', label: 'Indítva' }, { value: 'failed', label: 'Sikertelen' },
            ]} />
          )}
          <Select value={mode} onChange={setMode} options={[
            { value: 'all', label: 'Sandbox + éles' }, { value: 'sandbox', label: 'Csak sandbox' }, { value: 'live', label: 'Csak éles' },
          ]} />
        </div>
        <div className="mt-3 flex flex-wrap gap-4 text-[12.5px] text-[#B8B8C3]">
          <span>Sikeres: <b className="text-white">{stats.okN}</b></span>
          <span>Sikertelen: <b className="text-white">{stats.failedN}</b></span>
          <span>Folyamatban lévő visszatérítés: <b className={stats.pendingRefunds ? 'text-[#d9a514]' : 'text-white'}>{stats.pendingRefunds}</b></span>
        </div>
      </Card>

      {err && <Card className="mb-4"><span className="text-[#ef4444] text-[13px]">Nem sikerült betölteni: {err}</span></Card>}

      {pays === null ? <Spinner /> : tab === 'payments' ? (
        <Card pad={false} className="overflow-x-auto">
          {shown.length === 0 ? <div className="p-6"><Empty>Nincs fizetés a szűrésnek megfelelően.</Empty></div> : (
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[#8a8f99] text-[12px] border-b border-[#31383D]">
                  <th className="px-5 py-3 font-medium">Időpont</th>
                  <th className="px-3 py-3 font-medium">Játékos</th>
                  <th className="px-3 py-3 font-medium">Klub</th>
                  <th className="px-3 py-3 font-medium">Típus</th>
                  <th className="px-3 py-3 font-medium text-right">Összeg</th>
                  <th className="px-3 py-3 font-medium">Állapot</th>
                  <th className="px-3 py-3 font-medium">Elszámolás</th>
                  <th className="px-5 py-3 font-medium">Mód</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((p) => (
                  <tr key={p.id} onClick={() => setOpen(p)} className="border-b border-[#31383D]/60 hover:bg-white/[0.03] cursor-pointer">
                    <td className="px-5 py-3 whitespace-nowrap text-[#B8B8C3]">{dt(p.createdAt)}</td>
                    <td className="px-3 py-3 text-white">{who(p) || p.playerName || '—'}</td>
                    <td className="px-3 py-3">{clubName(p.clubId)}</td>
                    <td className="px-3 py-3">{p.kind === 'board' ? 'Teljes tábla' : 'Nyílt meccs'}</td>
                    <td className="px-3 py-3 text-right font-semibold text-white whitespace-nowrap">{ft(p.amount)}</td>
                    <td className="px-3 py-3"><Badge tone={p.orphan ? 'gold' : STATUS[p.status].tone}>{p.orphan ? 'Sikeres, de visszajár' : STATUS[p.status].label}</Badge></td>
                    <td className="px-3 py-3 text-[#B8B8C3] whitespace-nowrap">
                      {p.status !== 'succeeded' ? '—'
                        : p.captured ? `Lezárva · ${ft(p.keptAmount ?? p.amount)}${p.refundedAmount ? ` · vissza ${ft(p.refundedAmount)}` : ''}`
                          : 'Letétben'}
                    </td>
                    <td className="px-5 py-3"><Badge tone={p.mode === 'live' ? 'cyan' : 'gray'}>{p.mode === 'live' ? 'Éles' : 'Sandbox'}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      ) : (
        <Card pad={false} className="overflow-x-auto">
          {shownRefunds.length === 0 ? <div className="p-6"><Empty>Nincs visszatérítés a szűrésnek megfelelően.</Empty></div> : (
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[#8a8f99] text-[12px] border-b border-[#31383D]">
                  <th className="px-5 py-3 font-medium">Időpont</th>
                  <th className="px-3 py-3 font-medium">Játékos</th>
                  <th className="px-3 py-3 font-medium">Klub</th>
                  <th className="px-3 py-3 font-medium">Ok</th>
                  <th className="px-3 py-3 font-medium text-right">Visszajár</th>
                  <th className="px-3 py-3 font-medium text-right">Marad</th>
                  <th className="px-3 py-3 font-medium">Állapot</th>
                  <th className="px-5 py-3 font-medium">Határidő</th>
                </tr>
              </thead>
              <tbody>
                {shownRefunds.map((r) => (
                  <tr key={r.id} onClick={() => { const p = (pays ?? []).find((x) => x.id === r.paymentId); if (p) setOpen(p); }}
                    className="border-b border-[#31383D]/60 hover:bg-white/[0.03] cursor-pointer">
                    <td className="px-5 py-3 whitespace-nowrap text-[#B8B8C3]">{dt(r.createdAt)}</td>
                    <td className="px-3 py-3 text-white">{who(r) || '—'}</td>
                    <td className="px-3 py-3">{clubName(r.clubId)}</td>
                    <td className="px-3 py-3">{REASON[r.reason] ?? r.reason}</td>
                    <td className="px-3 py-3 text-right font-semibold text-white whitespace-nowrap">{ft(r.amount)}</td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">{ft(r.keep)}</td>
                    <td className="px-3 py-3">
                      <Badge tone={R_STATUS[r.status].tone}>{rLabel(r)}</Badge>
                      {r.status !== 'succeeded' && r.attempts > 0 && <span className="ml-2 text-[11.5px] text-[#8a8f99]">{r.attempts}. kísérlet</span>}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-[#B8B8C3]">{r.status === 'succeeded' ? '—' : dt(r.deadlineAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {open && (() => {
        const live = (pays ?? []).find((x) => x.id === open.id) ?? open; // élő állapot (pl. lezárás után)
        return <PaymentDetail p={live} who={who(live) || live.playerName} club={clubName(live.clubId)}
          refunds={refunds.filter((r) => r.paymentId === live.id)} onClose={() => setOpen(null)} />;
      })()}
    </div>
  );
}

function Settlement({ p, club }: { p: Pay; club: string }) {
  const base = baseOf(p);
  const sp = splitOf(base);
  const final = p.captured;
  const refunded = p.amount - base;
  const st = (done: string, wait: string) => (final ? done : wait);
  const line = (label: React.ReactNode, amount: number, note: string, opts: { strong?: boolean; minus?: boolean; indent?: boolean } = {}) => (
    <div className={`flex items-baseline justify-between gap-3 py-1.5 border-b border-[#31383D]/50 text-[13px] ${opts.indent ? 'pl-4' : ''}`}>
      <span className={opts.strong ? 'text-white font-semibold' : 'text-[#B8B8C3]'}>{label}<span className="block text-[11.5px] text-[#8a8f99]">{note}</span></span>
      <span className={`whitespace-nowrap ${opts.strong ? 'text-white font-bold' : 'text-white'}`}>{opts.minus ? '− ' : ''}{ft(amount)}</span>
    </div>
  );
  return (
    <div className="mt-4">
      <h4 className="mb-1 text-[13px] font-bold text-white uppercase italic">
        Elszámolás levezetése {final ? '' : <span className="ml-1 normal-case not-italic font-medium text-[11.5px] text-[#d9a514]">várható — még letétben</span>}
      </h4>
      {line('Befizetett összeg', p.amount, 'a játékos kártyájáról levonva')}
      {refunded > 0 && line('Visszatérítés a játékosnak', refunded, 'az eredeti fizetési módra', { minus: true })}
      {line(final ? 'Megtartott összeg' : 'Elszámolandó összeg', base, final ? 'ebből számolódik a megosztás' : 'lemondás esetén csökkenhet', { strong: true })}
      {line(`Klub — ${club}`, sp.club, st('átutalva a klub Barion-fiókjába (94%)', 'lezáráskor megy át a klub Barion-fiókjába (94%)'), { indent: true })}
      {line('Dartiqon', sp.dartiqon, st('a Dartiqon Barion-fiókjában (5%)', 'a Dartiqon Barion-fiókjában marad (5%)'), { indent: true })}
      {line('XP-keret', sp.xp, '1% — a Dartiqon fiókjában, külön nyilvántartva', { indent: true })}
      {line('↳ játékos XP', sp.xpPlayer, 'a fizető játékos XP-jóváírása', { indent: true })}
      {line('↳ Charityqon', sp.xpCharity, 'jótékonysági keret', { indent: true })}
      <p className="pt-2 text-[11.5px] text-[#8a8f99]">
        Ellenőrzés: {ft(sp.club)} + {ft(sp.dartiqon)} + {ft(sp.xp)} = {ft(sp.club + sp.dartiqon + sp.xp)}
      </p>
    </div>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 border-b border-[#31383D]/50 text-[13px]">
      <span className="text-[#8a8f99]">{k}</span><span className="text-white text-right break-all">{v}</span>
    </div>
  );
}

function PaymentDetail({ p, who, club, refunds, onClose }: { p: Pay; who: string; club: string; refunds: Refund[]; onClose: () => void }) {
  const [events, setEvents] = useState<Ev[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [reload, setReload] = useState(0);
  const canSettle = p.mode === 'sandbox' && p.status === 'succeeded' && !p.captured && !p.orphan;
  const doSettle = async () => {
    if (!window.confirm('Lezárod most ezt a teszt-letétet? A teljes összeg elszámolódik: a klub része átmegy a klub Barion-fiókjába.')) return;
    setBusy(true); setMsg(null);
    try {
      const st = await settleNow(p.id);
      setMsg({ ok: st !== 'failed', text: st === 'failed' ? 'A Barion elutasította a lezárást — részletek a naplóban.' : 'Lezárva. A klub része átutalva a klub Barion-fiókjába.' });
    } catch (e) {
      setMsg({ ok: false, text: e instanceof Error ? e.message : 'A lezárás nem sikerült.' });
    } finally {
      setBusy(false); setReload((x) => x + 1);
    }
  };
  useEffect(() => {
    getDocs(query(collection(db, 'payments', p.id, 'events'), orderBy('at', 'asc'), limit(100)))
      .then((s) => setEvents(s.docs.map((d) => { const v = d.data(); return { id: d.id, type: String(v.type ?? ''), at: toDate(v.at), data: v }; })))
      .catch(() => setEvents([]));
  }, [p.id, reload]);
  return (
    <Modal title="Fizetés részletei" onClose={onClose} width={640}>
      <div className="mb-4 flex flex-wrap gap-2">
        <Badge tone={STATUS[p.status].tone}>{STATUS[p.status].label}</Badge>
        <Badge tone={p.mode === 'live' ? 'cyan' : 'gray'}>{p.mode === 'live' ? 'Éles' : 'Sandbox'}</Badge>
        {p.orphan && <Badge tone="gold">Betelt / megszűnt hely — visszajár</Badge>}
      </div>
      <Row k="Játékos" v={who || '—'} />
      <Row k="Klub" v={club} />
      <Row k="Tétel" v={p.itemName || (p.kind === 'board' ? 'Teljes tábla' : 'Nyílt meccs')} />
      <Row k="Időpont (meccs)" v={dt(p.startAt)} />
      <Row k="Befizetve" v={ft(p.amount)} />
      <Row k="Elszámolás" v={p.status !== 'succeeded' ? '—' : p.captured ? 'Lezárva' : 'Letétben (a meccs után 3 órával zárul)'} />

      {p.status === 'succeeded' && <Settlement p={p} club={club} />}
      {p.failReason && <Row k="Sikertelenség oka" v={p.failReason} />}
      <Row k="Barion állapot" v={p.barionStatus || '—'} />
      <Row k="Barion PaymentId" v={p.barionPaymentId ?? '—'} />
      <Row k="Dartiqon azonosító" v={p.id} />

      {canSettle && (
        <div className="mt-4 p-3 rounded-xl border border-[#d9a514]/50 bg-[#d9a514]/5">
          <p className="text-[12.5px] text-[#B8B8C3] mb-2.5">
            Teszt (sandbox): a letét a meccs után 3 órával magától lezárul. Itt most azonnal lezárhatod, hogy lásd a klubnak járó átutalást.
          </p>
          <Button kind="primary" icon="CheckCircle" disabled={busy} onClick={() => void doSettle()}>
            {busy ? 'Lezárás…' : 'Lezárás most'}
          </Button>
        </div>
      )}
      {msg && <p className={`mt-3 text-[13px] ${msg.ok ? 'text-[#22c55e]' : 'text-[#ef4444]'}`}>{msg.text}</p>}

      {refunds.length > 0 && (
        <>
          <h4 className="mt-5 mb-2 text-[13px] font-bold text-white uppercase italic">Visszatérítések</h4>
          {refunds.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-3 py-1.5 text-[13px] border-b border-[#31383D]/50">
              <span>{REASON[r.reason] ?? r.reason} · vissza {ft(r.amount)} · marad {ft(r.keep)}</span>
              <Badge tone={R_STATUS[r.status].tone}>{rLabel(r)}</Badge>
            </div>
          ))}
          {refunds.some((r) => r.lastError) && (
            <p className="mt-2 text-[12px] text-[#ef4444] break-all">{refunds.find((r) => r.lastError)?.lastError}</p>
          )}
        </>
      )}

      <h4 className="mt-5 mb-2 text-[13px] font-bold text-white uppercase italic">Napló</h4>
      {events === null ? <Spinner /> : events.length === 0 ? <Empty>Nincs naplóbejegyzés.</Empty> : (
        <div className="space-y-1.5">
          {events.map((e) => (
            <div key={e.id} className="flex gap-3 text-[12.5px]">
              <span className="text-[#8a8f99] whitespace-nowrap">{dt(e.at)}</span>
              <span className="text-white">{EV_LABEL[e.type] ?? e.type}</span>
              {typeof e.data.error === 'string' && <span className="text-[#ef4444] break-all">{e.data.error}</span>}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
